export const CDM_FINDER_REPORT_TYPE = "cdm-finder";

// Curated ATC level-1/2 drug classes relevant to CDM / Prevention Programme
// conditions (Type 2 Diabetes, Asthma, COPD, cardiovascular disease, hypertension,
// atrial fibrillation, stroke/TIA). The finder matches on drugs_file.atc1/atc2, so
// only level 1/2 codes resolve — finer codes won't match.
// NOTE: this list is a sensible default and should be confirmed clinically.
export const CDM_FINDER_ATC_CLASSES = [
  { value: "A10", label: "A10 — Diabetes (antidiabetics)" },
  { value: "C09", label: "C09 — ACE inhibitors / ARBs" },
  { value: "C07", label: "C07 — Beta blockers" },
  { value: "C08", label: "C08 — Calcium channel blockers" },
  { value: "C03", label: "C03 — Diuretics" },
  { value: "C10", label: "C10 — Lipid-lowering (statins)" },
  { value: "C01", label: "C01 — Cardiac therapy" },
  { value: "B01", label: "B01 — Antithrombotics" },
  { value: "R03", label: "R03 — Respiratory (obstructive airway)" },
];

// Age-band presets sent to the finder as inclusive ageMin/ageMax (whole years).
export const CDM_FINDER_AGE_BANDS = [
  { value: "", label: "Any age" },
  { value: "18+", label: "18 and over", ageMin: 18 },
  { value: "18-69", label: "18–69", ageMin: 18, ageMax: 69 },
  { value: "45+", label: "45 and over", ageMin: 45 },
  { value: "45-69", label: "45–69", ageMin: 45, ageMax: 69 },
];

export const getFinderAgeBand = (value) =>
  CDM_FINDER_AGE_BANDS.find((band) => band.value === value) || null;

const findAgeBand = (value) =>
  CDM_FINDER_AGE_BANDS.find((band) => band.value === value);

// Age options per finder (spec Selection 2): Treatment & Prevention are 18+
// (18+ / 18–69); OCF is 45+ (45+ / 45–69) with an 18+ exception for a history
// suspicious of familial hypercholesterolaemia. "Any age" stays available.
export const getFinderAgeBandsForReviewType = (reviewType) => {
  const anyAge = findAgeBand("");

  if (reviewType === "OCF") {
    return [anyAge, findAgeBand("45+"), findAgeBand("45-69"), findAgeBand("18+")];
  }

  // CKD offers the full set (spec Selection 2: CKD additionally allows 45+/45–69).
  if (reviewType === "CKD") {
    return CDM_FINDER_AGE_BANDS;
  }

  if (reviewType === "CDM" || reviewType === "PP") {
    return [anyAge, findAgeBand("18+"), findAgeBand("18-69")];
  }

  return CDM_FINDER_AGE_BANDS;
};

// cdmStatus reuses the backend CdmProgrammeStatus enum. In finder results only
// NOT_ENROLLED and DEREGISTERED can occur (enrolled statuses are excluded), and a
// missing cdm_patients row is treated as NOT_ENROLLED. See KIN-1245.
export const CDM_STATUS_LABELS = {
  DEREGISTERED: "Previously deregistered",
  NOT_ENROLLED: "Not enrolled",
};

export const getCdmStatusLabel = (status) =>
  CDM_STATUS_LABELS[status] || CDM_STATUS_LABELS.NOT_ENROLLED;

// Optional guided entry to the finder, mirroring Socrates' programme-based
// finders ("New List → CDM / Prevention Programme / Opportunistic Case Finding
// → <condition>"). Picking a review type + condition is a shortcut that seeds
// the ICD-10 criteria; the free ICD-10 picker and ATC filter still apply.
export const CDM_FINDER_REVIEW_TYPES = [
  { value: "CDM", label: "Chronic Disease Management" },
  { value: "PP", label: "Prevention Programme" },
  { value: "OCF", label: "Opportunistic Case Finding" },
];

// Wording kept 1:1 with the spec's "Screen text that must appear on the GP
// Finders screen" (CDM Phase 4 - CDM Finder 24-11-2025). Keyed by review type;
// "" is our own generic lead-in (the spec defines no default text).
export const CDM_FINDER_REVIEW_TYPE_INFO = {
  "": "Finds active patients who may be suitable for a CDM / Prevention / OCF programme. Pick a review type (and condition), or search by ICD-10 code or medication class.",
  CDM: 'This finder will find patients that are potentially suitable for enrolling in the Treatment Programme. It will be divided into 2 types of cases, "probable cases" if the patient is found by disease codes and "possible cases" if the patient is found either because they are on medication or have had an investigation and results show that they are likely to have one of the diseases concerned. The Treatment Programme Finder should be able to find patients potentially with the following chronic diseases: Heart Failure, A Fib, Diabetes type 2, COPD/Asthma and Atherosclerotic Disease.',
  PP: "The Prevention Programme /Undiagnosed Finder seeks to identify patients likely to be enrolled in the Prevention Programme or possibly the Treatment Programme. The GP will decide which programme if any, that the patient should be entered into, after he reviews the patient's main record.",
  OCF: "The OCF Programme Finder seeks to identify patients likely to require an OCF Programme assessment. The GP will decide which programme if any, that the patient should be entered into, after he reviews the patient's main record.",
  // No dedicated "screen text" for CKD in the spec — this is a functional summary
  // of the CKD Finder (spec p.10): eGFR band routes the patient to a programme.
  CKD: "The CKD Finder identifies patients with chronic kidney disease. Enter an eGFR range: severe CKD (eGFR 0–29) is suitable for the Treatment Programme, moderate CKD (eGFR 30–59) for the Prevention Programme.",
};

// General text mandated by the CDM Project Team (Yolanda Siu, 2026-07) to appear
// on the main finder screen at all times. Wording is verbatim from that email.
export const CDM_FINDER_GENERAL_TEXT =
  "Remember that the CDM Treatment Programme is for those over 18 years, the Prevention Programme for hypertension/GDM/pre-eclampsia is currently for those over 18 years. The age for the remaining Prevention Programme conditions will be for those over 18 years once implemented during CDM Phase 4. The OCF Programme is for those aged 45 years and over with the exception of possible Familial Hypercholesterolaemia which is for those aged 18 years and over (Phase 4). The GP will select the appropriate age group in advance of running the finder search.";

// Shown under the finder when the Atherosclerotic condition is selected — again
// verbatim from the CDM Project Team email.
export const CDM_FINDER_ATHEROSCLEROTIC_TEXT =
  'There is also an "Atherosclerotic Finder" which is aimed at identifying people with this group of diseases only, this is a subset of the Treatment Programme finder, and includes Ischaemic Heart Disease, Peripheral Arterial Disease, CVD Stroke and CVD TIA.';

export const getFinderReviewTypeInfo = (reviewType) =>
  CDM_FINDER_REVIEW_TYPE_INFO[reviewType] || CDM_FINDER_REVIEW_TYPE_INFO[""];

// Condition → ICD-10 code(s) per review type. Codes are level-3 category codes
// (the finder matches on icd10 category), each { code, name }.
// NOTE: these mappings are a sensible default and MUST be confirmed clinically.
// Conditions without a meaningful diagnosis code (risk scores, lab values, risk
// factors) are listed with empty codes so they are not silently dropped.
export const CDM_FINDER_CONDITIONS_BY_TYPE = {
  CDM: [
    { value: "HEART_FAILURE", label: "Heart Failure", icd10Codes: [{ code: "I50", name: "Heart failure" }] },
    { value: "ATRIAL_FIBRILLATION", label: "Atrial Fibrillation", icd10Codes: [{ code: "I48", name: "Atrial fibrillation and flutter" }] },
    { value: "DIABETES_TYPE_2", label: "Diabetes Type 2", icd10Codes: [{ code: "E11", name: "Type 2 diabetes mellitus" }] },
    { value: "ASTHMA", label: "Asthma", icd10Codes: [{ code: "J45", name: "Asthma" }] },
    { value: "COPD", label: "COPD", icd10Codes: [{ code: "J44", name: "Chronic obstructive pulmonary disease" }] },
    { value: "ISCHAEMIC_HEART_DISEASE", label: "Ischaemic Heart Disease", icd10Codes: [{ code: "I25", name: "Chronic ischaemic heart disease" }] },
    { value: "STROKE", label: "Cerebrovascular Disease — Stroke", icd10Codes: [{ code: "I63", name: "Cerebral infarction" }] },
    { value: "TIA", label: "Cerebrovascular Disease — TIA", icd10Codes: [{ code: "G45", name: "Transient cerebral ischaemic attacks" }] },
    // Combined finders from the p.18 schematic (one finder = several diseases).
    { value: "ASTHMA_COPD", label: "Asthma / COPD", icd10Codes: [
      { code: "J45", name: "Asthma" },
      { code: "J46", name: "Status asthmaticus" },
      { code: "J44", name: "Chronic obstructive pulmonary disease" },
    ] },
    { value: "ATHEROSCLEROTIC", label: "Atherosclerotic Disease", icd10Codes: [
      { code: "I20", name: "Angina pectoris" },
      { code: "I21", name: "Acute myocardial infarction" },
      { code: "I22", name: "Subsequent myocardial infarction" },
      { code: "I23", name: "Complications following acute MI" },
      { code: "I24", name: "Other acute ischaemic heart disease" },
      { code: "I25", name: "Chronic ischaemic heart disease" },
      { code: "I73", name: "Peripheral arterial disease" },
      { code: "I74", name: "Arterial embolism / thrombosis" },
      { code: "I63", name: "CVD — Cerebral infarction (stroke)" },
      { code: "I64", name: "CVD — Stroke, unspecified" },
      { code: "I67", name: "CVD — Other cerebrovascular disease" },
      { code: "G45", name: "CVD — TIA" },
    ] },
  ],
  PP: [
    { value: "HYPERTENSION", label: "Hypertension (Stage 1–3)", icd10Codes: [{ code: "I10", name: "Essential (primary) hypertension" }] },
    { value: "PRE_DIABETES", label: "Pre-Diabetes", icd10Codes: [{ code: "R73", name: "Elevated blood glucose level" }] },
    { value: "GESTATIONAL_DIABETES", label: "Gestational Diabetes", icd10Codes: [{ code: "O24", name: "Diabetes mellitus in pregnancy" }] },
    { value: "PRE_ECLAMPSIA", label: "Pre-eclampsia", icd10Codes: [{ code: "O14", name: "Pre-eclampsia" }] },
    { value: "QRISK_GTE_20", label: "QRISK3 ≥ 20%", icd10Codes: [] },
    { value: "PREVIOUS_HIGH_BNP", label: "Previous high BNP / NT-proBNP", icd10Codes: [] },
    // Combined GDM / Pre-eclampsia finder from the p.18 schematic.
    { value: "GDM_PRE_ECLAMPSIA", label: "GDM / Pre-eclampsia", icd10Codes: [
      { code: "O14", name: "Pre-eclampsia" },
      { code: "O15", name: "Eclampsia" },
      { code: "O16", name: "Unspecified maternal hypertension" },
      { code: "O24", name: "Gestational diabetes" },
    ] },
  ],
  // OCF Finder coded-condition list — the ICD-10 codes exactly as listed on p.14
  // of the spec ("This is the list of codes for the OCF Finder"), one option per
  // code. Baseline signals (BMI, current smoker, ethnicity) and blood results
  // (cholesterol, NT-proBNP) are not ICD conditions — they live in the advanced
  // filters. NOTE: mappings are a clinical default and MUST be confirmed clinically.
  OCF: [
    { value: "E66", label: "Obesity", icd10Codes: [{ code: "E66", name: "Obesity" }] },
    { value: "E78", label: "Hyperlipidaemias", icd10Codes: [{ code: "E78", name: "Hyperlipidaemias" }] },
    { value: "F00", label: "Dementias", icd10Codes: [{ code: "F00", name: "Dementia in Alzheimer's disease" }] },
    { value: "F01", label: "Dementias", icd10Codes: [{ code: "F01", name: "Vascular dementia" }] },
    { value: "F02", label: "Dementias", icd10Codes: [{ code: "F02", name: "Dementia in other diseases classified elsewhere" }] },
    { value: "F03", label: "Dementia unspecified", icd10Codes: [{ code: "F03", name: "Unspecified dementia" }] },
    { value: "F06", label: "Other mental disorders", icd10Codes: [{ code: "F06", name: "Other mental disorders due to brain damage" }] },
    { value: "F20", label: "Schizophrenias", icd10Codes: [{ code: "F20", name: "Schizophrenia" }] },
    { value: "F29", label: "Psychosis", icd10Codes: [{ code: "F29", name: "Unspecified psychosis" }] },
    { value: "F31", label: "Bipolar disorder", icd10Codes: [{ code: "F31", name: "Bipolar affective disorder" }] },
    { value: "F32", label: "Depressive illnesses", icd10Codes: [{ code: "F32", name: "Depressive episode" }] },
    { value: "E78_01", label: "Familial Hypercholesterolaemia", icd10Codes: [{ code: "E78.01", name: "Familial hypercholesterolaemia" }] },
  ],
  // CKD Finder codes (spec p.10): N00–N19 renal disease incl. N18 CKD stages and
  // Z99.2 (dialysis dependence). The eGFR range (severe ≤30 → Treatment, moderate
  // 30–59 → Prevention) is a separate user input, sent as minEgfr/maxEgfr.
  CKD: [
    { value: "CKD", label: "Chronic Kidney Disease", icd10Codes: [
      { code: "N12", name: "Tubulo-interstitial nephritis" },
      { code: "N13", name: "Obstructive and reflux uropathy" },
      { code: "N14", name: "Drug- and heavy-metal-induced nephropathy" },
      { code: "N15", name: "Other renal tubulo-interstitial diseases" },
      { code: "N16", name: "Renal tubulo-interstitial disorders in other diseases" },
      { code: "N17", name: "Acute kidney failure" },
      { code: "N18", name: "Chronic kidney disease" },
      { code: "N19", name: "Unspecified kidney failure" },
      { code: "Z99", name: "Dependence on renal dialysis" },
    ] },
  ],
};

const unionConditionCodes = (reviewType) => {
  const groups = reviewType
    ? [CDM_FINDER_CONDITIONS_BY_TYPE[reviewType] || []]
    : Object.values(CDM_FINDER_CONDITIONS_BY_TYPE);

  const codes = groups.flat().flatMap((item) => item.icd10Codes ?? []);
  // Distinct conditions can share an ICD-10 category (e.g. Dyslipidaemia and
  // Familial Hypercholesterolaemia both → E78) — dedupe so "Any condition"
  // doesn't send the same code twice.
  return codes.filter(
    (code, index) => codes.findIndex((other) => other.code === code.code) === index
  );
};

// The ICD-10 code objects seeded by the guided shortcut. `conditionValues` is a
// list of selected condition values (the Condition control is a multi-select):
// - one or more conditions → the union of those conditions' codes
// - none selected → the union of the whole programme's codes ("any")
// - no programme → the union across every programme
// so selecting a programme (with or without conditions) still fires a search.
export const getFinderConditionCodes = (reviewType, conditionValues) => {
  const values = Array.isArray(conditionValues)
    ? conditionValues
    : conditionValues
      ? [conditionValues]
      : [];

  if (reviewType && values.length) {
    const codes = (CDM_FINDER_CONDITIONS_BY_TYPE[reviewType] || [])
      .filter((item) => values.includes(item.value))
      .flatMap((item) => item.icd10Codes ?? []);

    return codes.filter(
      (code, index) =>
        codes.findIndex((other) => other.code === code.code) === index
    );
  }

  return unionConditionCodes(reviewType);
};

// Finder navigation per the spec's p.18 schematic: a single grouped picker with
// 3 groups, each listing its individual finders. Selecting one seeds the review
// type + condition (and, for CKD, a default eGFR band). `key` is the stored
// selection; the register entries live on the CDM Register tab, not here.
export const CDM_FINDER_GROUPS = [
  {
    label: "Chronic Disease Treatment Finders",
    finders: [
      { key: "CDM/HEART_FAILURE", label: "Heart Failure Finder", reviewType: "CDM", condition: "HEART_FAILURE" },
      { key: "CDM/ATRIAL_FIBRILLATION", label: "Atrial Fibrillation Finder", reviewType: "CDM", condition: "ATRIAL_FIBRILLATION" },
      { key: "CDM/DIABETES_TYPE_2", label: "Diabetes Type 2 Finder", reviewType: "CDM", condition: "DIABETES_TYPE_2" },
      { key: "CDM/ASTHMA_COPD", label: "Asthma / COPD Finder", reviewType: "CDM", condition: "ASTHMA_COPD" },
      { key: "CDM/ATHEROSCLEROTIC", label: "Atherosclerotic Disease Finder", reviewType: "CDM", condition: "ATHEROSCLEROTIC" },
    ],
  },
  {
    label: "OCF / Prevention Finders",
    finders: [
      { key: "PP/ANY", label: "Prevention / Undiagnosed Finder", reviewType: "PP", condition: "" },
      { key: "PP/GDM_PRE_ECLAMPSIA", label: "GDM / Pre-eclampsia Finder", reviewType: "PP", condition: "GDM_PRE_ECLAMPSIA" },
      { key: "OCF/ANY", label: "OCF Finder", reviewType: "OCF", condition: "" },
    ],
  },
  {
    label: "CKD Finders",
    finders: [
      { key: "CKD/PREVENTION", label: "CKD Finder – Prevention", reviewType: "CKD", condition: "CKD", egfrMin: 30, egfrMax: 59 },
      { key: "CKD/TREATMENT", label: "CKD Finder – Treatment", reviewType: "CKD", condition: "CKD", egfrMin: 0, egfrMax: 29 },
    ],
  },
];

// Shape the groups for the shared Select's `groups` prop (label + options).
export const CDM_FINDER_GROUP_OPTIONS = CDM_FINDER_GROUPS.map((group) => ({
  label: group.label,
  options: group.finders.map((finder) => ({
    value: finder.key,
    label: finder.label,
  })),
}));

const ALL_FINDERS = CDM_FINDER_GROUPS.flatMap((group) => group.finders);

export const getFinderPreset = (key) =>
  ALL_FINDERS.find((finder) => finder.key === key) || null;
